import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { STATUS_BG_COLORS, STATUS_COLORS } from "@/utils/constants";
import { ReactNode } from "react";

interface Props {
  value: string;
  status: string;
  text: string;
  total?: number;
  hasTotal?: boolean;
  button?: ReactNode;
  subtitle?: string;
  isLoading?: boolean;
  percentage?: number;
}

export const COLOR_GROUPS = {
  green: {
    bg: "bg-[#A9D3421A]",
    dot: "bg-[#A9D342]",
  },
  lightGreen: {
    bg: "bg-[#C0E57A1A]",
    dot: "bg-[#C0E57A]",
  },
  red: {
    bg: "bg-[#F871711A]",
    dot: "bg-[#F87171]",
  },
  gray: {
    bg: "bg-[#27272A]",
    dot: "bg-[#A1A1AA]",
  },
  yellow: {
    bg: "bg-[#FACC151A]",
    dot: "bg-[#FACC15]",
  },
  blue: {
    bg: "bg-[#60A5FA1A]",
    dot: "bg-[#60A5FA]",
  },
  lightYellow: {
    bg: "bg-[rgba(250,204,21,0.10)]",
    dot: "bg-[#FDE047]",
  },
};

export default function CardCurrency({
  value,
  status,
  text,
  total,
  hasTotal,
  button,
  subtitle,
  isLoading,
  percentage,
}: Props) {
  const statusColor =
    STATUS_COLORS[status as keyof typeof STATUS_COLORS] || STATUS_COLORS.paid;

  return (
    <section className="bg-[#09090b] border w-full rounded-lg p-4 sm:p-6 flex sm:items-center max-sm:flex-col max-sm:gap-3">
      <div className="flex items-stretch flex-1 min-w-0 min-h-0">
        <div
          className={`w-1 self-stretch min-h-[40px] rounded-full flex-shrink-0 ${STATUS_BG_COLORS[status as keyof typeof STATUS_BG_COLORS] ?? STATUS_BG_COLORS.paid}`}
        />
        <div className="ml-3 sm:ml-5 flex-1">
          {isLoading ? (
            <Skeleton className="h-14 w-full" />
          ) : (
            <article>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm text-zinc-400">{text}</p>
                {hasTotal && (
                  <Badge
                    variant="secondary"
                    className="text-xs font-medium flex items-center justify-center whitespace-nowrap"
                  >
                    Total: {total ? total : 0}
                  </Badge>
                )}
                {percentage !== undefined && percentage > 0 && (
                  <Badge
                    variant="outline"
                    className="text-xs font-semibold border-0 px-2"
                    style={{
                      backgroundColor: `${statusColor}15`,
                      color: statusColor,
                    }}
                  >
                    {percentage.toFixed(2)}%
                  </Badge>
                )}
              </div>
              <p className="text-xl sm:text-2xl font-semibold mt-0.5 sm:mt-1 break-words">
                {value}
              </p>
              {subtitle && (
                <p className="text-xs text-zinc-500 mt-0.5">{subtitle}</p>
              )}
            </article>
          )}
        </div>
      </div>
      {button && (
        <div className="flex items-center justify-center sm:justify-end shrink-0 sm:ml-4">
          {button}
        </div>
      )}
    </section>
  );
}
